import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import isTextCropped from '@lib/utils/isTextCropped';

interface Options {
  /** Force the tooltip off regardless of cropping (e.g. a per-instance opt-out). */
  disabled?: boolean;
}

interface Result<T extends HTMLElement> {
  /** Attach to the text element whose overflow should be measured. */
  ref: RefObject<T | null>;
  /** True when no tooltip should show (text not cropped, empty, or force-disabled). */
  isTooltipDisabled: boolean;
  /** Re-measure cropping on demand (e.g. on mouse enter when width may have changed). */
  checkCropped: () => void;
}

/**
 * Shared "tooltip only when text is cropped" recipe: measure the referenced
 * element with `isTextCropped` and expose whether a tooltip should be disabled.
 * Replaces the ref + useState + useEffect + isTextCropped block duplicated across
 * CroppedText, Chip, Button, TextCell, TextLinkCell and TimeCountdownCell.
 */
export default function useTextCroppedTooltip<
  T extends HTMLElement = HTMLElement,
>(content?: unknown, options?: Options): Result<T> {
  const { disabled = false } = options ?? {};
  const ref = useRef<T>(null);
  const [isTooltipDisabled, setIsTooltipDisabled] = useState(true);

  const checkCropped = useCallback(() => {
    setIsTooltipDisabled(disabled || !content || !isTextCropped(ref.current));
  }, [content, disabled]);

  useEffect(() => {
    checkCropped();
  }, [checkCropped]);

  return { ref, isTooltipDisabled, checkCropped };
}
